import { z } from 'zod';
import { AddonProduct } from '@/constants/AddonProduct';
import { AgreementStatus } from '@/constants/AgreementStatus';
import { CreditRejectReasons, LevlaRejectReasons } from '@/constants/RejectReasons';

export const createApplicationResponseSchema = z.object({
  applicationId: z.string(),
  orderId: z.string(),
  autoStartToken: z.string(),
  userId: z.string(), // Used for tracking
});

const applicantSchema = z.object({
  email: z.string(),
  nationalNumber: z.string(),
  addonProducts: z
    .array(z.enum([AddonProduct.PAYMENT_PROTECTION_INSURANCE, AddonProduct.ID_PROTECT]))
    .optional(),
});

const channelSchema = z.object({
  name: z.string().optional(),
  utmTags: z
    .object({
      utm_campaign: z.string().nullable(),
      utm_source: z.string().nullable(),
      utm_medium: z.string().nullable(),
      utm_content: z.string().nullable(),
      utm_term: z.string().nullable(),
      utm_custom_tags: z.record(z.string(), z.string()),
    })
    .optional(),
});

export const creditOfferResponseSchema = z.object({
  status: z.literal(AgreementStatus.CREDIT_OFFER),
  openBankingAssessment: z.enum([
    'NOT_APPLICABLE', //e.g. when the offer comes from a broker
    'OPTED_OUT',
    'OPTED_IN_DATA_PROCESSED',
    'OPTED_IN_DATA_NOT_PROCESSED',
  ]),
  productTier: z.object({
    id: z.string(),
    creditLimit: z.number(),
    interestRate: z.number(),
    effectiveInterestRate: z.number(),
    installmentAmount: z.number(),
    numberOfInstallments: z.number(),
    totalCost: z.number(),
    totalRepaid: z.number(),
    fees: z.object({
      setupFee: z.number(),
      installmentFee: z.number(),
      withdrawalFee: z.number(),
    }),
  }),
  applicant: applicantSchema,
  channel: channelSchema,
});

const rejectedChannelSchema = z.object({
  type: z.enum(['BROKER', 'AFFILIATE', 'PARTNER', 'DIRECT']).optional(),
  name: z.string().optional(),
});

export const rejectedSchema = z
  .object({
    status: z.literal(AgreementStatus.REJECTED),
    userName: z.string(),
    channel: rejectedChannelSchema.optional(),
  })
  .and(
    z
      .union([
        z.object({
          causeOfReject: z.enum([
            CreditRejectReasons.BANKID_ISSUE_DATE,
            CreditRejectReasons.EXCEED_NUMBER_OF_LOANS,
            CreditRejectReasons.EXISTING_CREDIT,
            CreditRejectReasons.EXISTING_LOAN,
            CreditRejectReasons.KALP_REJECTED,
            CreditRejectReasons.OVERDUE_PAYMENTS,
            CreditRejectReasons.PARTNER_REJECTED,
            CreditRejectReasons.PSD_LOW_INCOME,
            CreditRejectReasons.TERMINATED_LOAN,
            CreditRejectReasons.USER_BLOCKED,
            CreditRejectReasons.TERMINATED_AGREEMENT,
            CreditRejectReasons.UC_SCORE_MISSING,
            CreditRejectReasons.UC_SERVICE_ERROR,
            'UNKNOWN',
          ]),
        }),
        z.object({
          causeOfReject: z.literal(CreditRejectReasons.BISNODE_REJECTED),
          rejectCode: z.string().array(),
        }),
        z.object({
          causeOfReject: z.literal(CreditRejectReasons.RECENT_BISNODE_CALL),
          dateToReapply: z.string(),
        }),
      ])
      .catch({ causeOfReject: 'UNKNOWN' }),
  );

export const eligibilityCheckerFailedSchema = z.object({
  status: z.literal(AgreementStatus.ELIGIBILITY_CHECKER_FAILED),
});

export const postEvaluationFailedSchema = z.object({
  status: z.literal(AgreementStatus.POST_EVALUATION_FAILED),
});

const openBankingRecoverySchema = z.object({
  status: z.literal(AgreementStatus.OPEN_BANKING_RECOVERY),
});

const incomeVerificationRecoverySchema = z.object({
  status: z.literal(AgreementStatus.INCOME_VERIFICATION_RECOVERY),
});

export const levlaRejectedSchema = z
  .object({
    status: z.literal(AgreementStatus.LEVLA_REJECTED),
    userName: z.string(),
    grantedAddonProducts: z.array(
      z.enum([AddonProduct.PAYMENT_PROTECTION_INSURANCE, AddonProduct.ID_PROTECT]),
    ),
  })
  .and(
    z
      .union([
        z.object({
          causeOfReject: z.enum([
            LevlaRejectReasons.DIRECT_DEBIT_MANDATE_NOT_FOUND,
            LevlaRejectReasons.ELIGIBILITY_CHECKER_REJECTED,
            LevlaRejectReasons.INTERNAL_SCORE,
            LevlaRejectReasons.MAX_PRODUCT,
            LevlaRejectReasons.ONGOING_CHARGE,
            LevlaRejectReasons.OVERDUE_PAYMENTS,
            LevlaRejectReasons.PAYMENTS_WITH_FEES,
            LevlaRejectReasons.TOO_MANY_INQUIRIES,
            LevlaRejectReasons.UC_SCORE_MISSING,
            LevlaRejectReasons.UC_SERVICE_ERROR,
            LevlaRejectReasons.PAYMENT_FREE_MONTH,
            'UNKNOWN',
          ]),
        }),
        z.object({
          causeOfReject: z.literal(LevlaRejectReasons.RECENT_BISNODE_CALL),
          dateToReapply: z.string(),
        }),
      ])
      .catch({ causeOfReject: 'UNKNOWN' }),
  );

const statusProductTierSchema = z.object({
  id: z.string(),
  creditLimit: z.number(),
  creditAmountUsed: z.number(),
  interestRate: z.number(),
  installmentAmount: z.number(),
  numberOfInstallments: z.number(),
  totalCost: z.number(),
});

const offerFeesSchema = z.object({
  setupFee: z.number(),
  installmentFee: z.number(),
});

export const levlaOfferSchema = z.object({
  status: z.literal(AgreementStatus.LEVLA_OFFER),
  currentProductTier: statusProductTierSchema,
  offeredProductTier: statusProductTierSchema.extend({
    fees: offerFeesSchema,
  }),
  applicant: applicantSchema,
  channel: channelSchema,
});

export const loanOfferSchema = z.object({
  status: z.literal(AgreementStatus.LOAN_OFFER),
  productTier: z.object({
    id: z.string(),
    loanAmount: z.number(),
    payoutAmount: z.number(),
    interestRate: z.number(),
    installmentAmount: z.number(),
    numberOfInstallments: z.number(),
    totalCost: z.number(),
    totalRepaid: z.number(),
    effectiveInterestRate: z.number(),
    fees: offerFeesSchema,
  }),
  applicant: applicantSchema,
  channel: channelSchema,
});

export const loanIdProtectOfferSchema = z.object({
  status: z.literal(AgreementStatus.LOAN_ID_PROTECT_OFFER),
  productTier: z.object({
    id: z.string(),
    loanAmount: z.number(),
    interestRate: z.number(),
    numberOfInstallments: z.number(),
    installmentAmount: z.number(),
    totalCost: z.number(),
    payoutAmount: z.number(),
    idProtectDurationInMonths: z.number(),
    idProtectCost: z.number(),
    originalIdProtectCost: z.number(),
    effectiveInterestRate: z.number(),
    totalRepaid: z.number(),
    fees: offerFeesSchema,
  }),
  applicant: applicantSchema,
  channel: channelSchema,
});

export const loanPartnerProductTierSchema = z.object({
  id: z.string(),
  productName: z.enum(['PREMIUM', 'PLUS', 'BASE']),
  loanAmount: z.number(),
  numberOfInstallments: z.number(),
  installmentAmount: z.number(),
  totalCost: z.number(),
  totalRepaid: z.number(),
  isEligible: z.boolean(),
  interestRate: z.number(),
  effectiveInterestRate: z.number(),
  fees: z.object({
    setupFee: z.number(),
    originalSetupFee: z.number(),
    installmentFee: z.number(),
  }),
});

export const loanPartnerOfferSchema = z.object({
  status: z.literal(AgreementStatus.LOAN_PARTNER_OFFER),
  partnerId: z.string(),
  productTiers: z.array(loanPartnerProductTierSchema).min(1),
  applicant: applicantSchema,
  channel: channelSchema,
});

const manualApprovalSchema = z.object({
  status: z.literal(AgreementStatus.MANUAL_APPROVAL),
});

const assessmentOutcomeSchemas = [
  creditOfferResponseSchema,
  rejectedSchema,
  eligibilityCheckerFailedSchema,
  postEvaluationFailedSchema,
  openBankingRecoverySchema,
  incomeVerificationRecoverySchema,
  levlaRejectedSchema,
  levlaOfferSchema,
  loanOfferSchema,
  loanIdProtectOfferSchema,
  loanPartnerOfferSchema,
  manualApprovalSchema,
] as const;

export const startApplicationAssessmentResponseSchema = z.union(assessmentOutcomeSchemas);

export const otherStatusResponseSchema = z.object({
  status: z.enum([
    AgreementStatus.EXPIRED,
    AgreementStatus.SIGNED,
    AgreementStatus.SIGNING_IN_PROGRESS,
    AgreementStatus.EVALUATION_IN_PROGRESS,
    AgreementStatus.EVALUATION_FAILED,
    AgreementStatus.FAILED,
  ]),
});

const createdStatusSchema = z.object({
  status: z.literal(AgreementStatus.CREATED),
  assessmentMode: z.enum(['DEFAULT', 'OPEN_BANKING_OPTIONAL', 'OPEN_BANKING_REQUIRED']),
});

export const getApplicationResponseSchema = z.union([
  ...assessmentOutcomeSchemas,
  createdStatusSchema,
  otherStatusResponseSchema,
]);

export const bankIdOrderResponseSchema = z.object({
  orderId: z.string(),
  autoStartToken: z.string(),
});

export const acceptOfferResponseSchema = z.object({
  authToken: z.string(),
  agreementId: z.string(),
});

export const createOpenBankingUrlResponseSchema = z.object({
  url: z.url(),
});
